import { useState, FC } from 'react';
import { Grid2, Typography, Button, Box } from '@mui/material';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';

import { ApiError } from '@libs/types';
import { PaymentCreateCustomerProfileDto } from '@libs/dto';
import { usePayment } from '@carcareers/hooks';
import { useForm } from '@carcareers/contexts';
import { TextInput } from '../Input/TextInput';
import { LocationInput } from '../Input/LocationInput';
import { CheckBoxInput } from '../Input/CheckBoxInput';

interface AddPaymentFormProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export const AddPaymentForm: FC<AddPaymentFormProps> = ({ onSuccess, onClose }) => {
  const { data, setData, setErrors } = useForm<PaymentCreateCustomerProfileDto>();
  const { createPayment } = usePayment();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: ApiError[] = [];

    if (!data.cardNumber || data.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.push({ property: 'cardNumber', errors: ['Valid card number is required'] });
    }

    if (!data.cardCode || data.cardCode.length < 3) {
      newErrors.push({ property: 'cardCode', errors: ['Valid security code is required'] });
    }

    if (!data.expirationDate) {
      newErrors.push({ property: 'expirationDate', errors: ['Expiration date is required'] });
    }

    if (!data.firstName.trim()) {
      newErrors.push({ property: 'firstName', errors: ['First name is required'] });
    }

    if (!data.lastName.trim()) {
      newErrors.push({ property: 'lastName', errors: ['Last name is required'] });
    }

    if (!data.address.trim()) {
      newErrors.push({ property: 'address', errors: ['Billing address is required'] });
    }

    setErrors(newErrors as ApiError[]);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        cardNumber: data.cardNumber.replace(/\s/g, ''),
      };

      const response = await createPayment(payload);

      if (response && 'data' in response) {
        onSuccess?.();
      }
    } catch (error) {
      console.error('Error creating payment profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CreditCardIcon sx={{ mr: 2, fontSize: 28 }} />
        <Typography variant="h5">Add Payment Method</Typography>
      </Box>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12 }}>
          <TextInput label="Card Number" name="cardNumber" required fullWidth/>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <DatePicker
            views={['year', 'month']}
            label="Expiration"
            minDate={dayjs(new Date())}
            onChange={(value) => setData('expirationDate', dayjs(value).format('MM/YY'))}
            format="MM/YY"
            sx={{ width: '100%' }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <TextInput label="Security Code" name="cardCode" required fullWidth />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <TextInput label="First Name" name="firstName" required fullWidth />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <TextInput label="Last Name" name="lastName" required fullWidth />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <LocationInput label="Billing Address" name="address" required fullWidth />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <CheckBoxInput label="Set as default payment method" name="defaultPaymentProfile" />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Payment Method'}
            </Button>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};
